import { createSlice, type PayloadAction } from "@reduxjs/toolkit"
import { countBy, isPlainObject, isString } from "lodash"
import { v4 as uuidv4 } from "uuid"
import { isModuleTypeRegistered } from "../modules/registry"
import { MODULE_TYPES, type DefaultModuleDescriptor } from "../modules/types"
import { DEFAULT_FACTORS_THEMATIC_CONTENT_SET_ID, DEFAULT_THEMATIC_SORT_METRIC_ID } from "../modules/constants"
import { warn, info } from "helpers/dev-logger"
import { log } from "helpers/logger"
import type { ModuleType, ModuleConfig, DashboardModule } from "../modules/types"

export interface PanelsState {
  modules: DashboardModule[]
  isPreferencesLoaded: boolean
}

export const EMPTY_PANELS_STATE: PanelsState = { modules: [], isPreferencesLoaded: false }
export const EMPTY_MODULES: DashboardModule[] = []

/** Keyed by dashboardId. Partial forces TS to narrow absent keys to undefined. */
export type PanelsRootState = Partial<Record<string, PanelsState>>

const BASE_DEFAULT_MODULE_DESCRIPTORS: DefaultModuleDescriptor[] = [
  { type: MODULE_TYPES.ASSET_VIEW, initialView: "summary" },
  { type: MODULE_TYPES.GRAPH, initialView: "performance", title: "Graph" },
  { type: MODULE_TYPES.KPI, initialTab: "summary", title: "KPI" },
  { type: MODULE_TYPES.KPI, initialTab: "decisions", title: "Decision" },
  { type: MODULE_TYPES.FACTORS, initialView: "risk", selectedTableLevels: ["style"] },
]

const THEMATIC_FACTORS_DEFAULT_DESCRIPTOR: DefaultModuleDescriptor = {
  type: MODULE_TYPES.FACTORS,
  initialView: DEFAULT_FACTORS_THEMATIC_CONTENT_SET_ID,
  sortMetricId: DEFAULT_THEMATIC_SORT_METRIC_ID,
  title: "Thematic Betas",
}

function getDefaultModuleDescriptors(hasThematicBetasContentSet?: boolean): DefaultModuleDescriptor[] {
  if (!hasThematicBetasContentSet) {
    return [...BASE_DEFAULT_MODULE_DESCRIPTORS]
  }
  return [...BASE_DEFAULT_MODULE_DESCRIPTORS, THEMATIC_FACTORS_DEFAULT_DESCRIPTOR]
}

export function createDefaultModules(hasThematicBetasContentSet?: boolean): DashboardModule[] {
  return getDefaultModuleDescriptors(hasThematicBetasContentSet).map(({ type, ...config }) => ({
    id: uuidv4(),
    type,
    config,
  }))
}

/** Compares modules as a multiset (type counts, not order) so duplicates like two KPI panels are handled correctly. */
export function isDefaultModulesSnapshot(
  modules: DashboardModule[],
  hasThematicBetasContentSet = false,
): boolean {
  const expectedTypes = getDefaultModuleDescriptors(hasThematicBetasContentSet).map(
    (descriptor) => descriptor.type,
  )
  if (modules.length !== expectedTypes.length) {
    return false
  }

  const remainingTypeCounts = countBy(expectedTypes)

  for (const module of modules) {
    if (module.config?.isSecondary) {
      return false
    }
    const remainingCount = remainingTypeCounts[module.type] ?? 0
    if (remainingCount === 0) {
      return false
    }
    remainingTypeCounts[module.type] = remainingCount - 1
  }

  return Object.values(remainingTypeCounts).every((count) => count === 0)
}

function ensureInstance(state: PanelsRootState, dashboardId: string): PanelsState {
  if (!state[dashboardId]) {
    state[dashboardId] = { modules: createDefaultModules(), isPreferencesLoaded: false }
  }
  return state[dashboardId] as PanelsState
}

function hasModuleShape(value: unknown): value is { id: string, type: string, config?: unknown } {
  if (!isPlainObject(value)) return false
  const obj = value as Record<string, unknown>
  return "id" in obj && "type" in obj && isString(obj.id) && isString(obj.type)
}

function validateModule(module: unknown): DashboardModule | null {
  if (!hasModuleShape(module)) return null

  if (!isModuleTypeRegistered(module.type)) {
    warn(`Module type "${module.type}" is not registered, removing module`)
    return null
  }

  // Persisted config is untyped at the JSON boundary; narrow each field we
  // pattern-match on with `typeof` instead of trusting the `ModuleConfig` cast.
  // A persisted `isPinning: "true"` (string) or `hasError: 1` (number) must not
  // silently take the boolean branches below.
  let config: ModuleConfig = {}
  if (module.config !== undefined) {
    if (isPlainObject(module.config)) {
      config = { ...(module.config as Record<string, unknown>) } as ModuleConfig
    } else {
      warn(`Module config must be an object, got ${typeof module.config}`)
    }
  }

  const isPinning = config.isPinning === true
  const pinnedId = typeof config.pinnedId === "string" ? config.pinnedId : undefined
  const hasError = config.hasError === true

  if (isPinning) {
    warn(`Module ${module.id} has stale isPinning flag, cleaning up`)
    if (pinnedId) {
      config = { ...config, isPinning: false }
    } else {
      warn(`Module ${module.id} has isPinning=true but no pinnedId, removing incomplete module`)
      return null
    }
  }

  // Never persist duplicateFromPanelId: it is only for the initial copy.
  if (Object.prototype.hasOwnProperty.call(config, "duplicateFromPanelId")) {
    config = { ...config }
    delete config.duplicateFromPanelId
  }

  // `floating` is only used when adding a panel (open in a floating window). Drop it from
  // persisted module config — floating placement is stored in the layout snapshot, not here.
  if (Object.prototype.hasOwnProperty.call(config, "floating")) {
    config = { ...config }
    delete config.floating
  }

  if (module.type === MODULE_TYPES.AI_ARTIFACT && hasError && !pinnedId) {
    warn(`Module ${module.id} has hasError=true but no pinnedId, removing failed pin module`)
    return null
  }

  return { id: module.id, type: module.type, config }
}

export function validateModules(
  modules: unknown,
  hasThematicBetasContentSet?: boolean,
): { valid: DashboardModule[], wasTruncated: boolean } {
  if (!Array.isArray(modules)) {
    return { valid: createDefaultModules(hasThematicBetasContentSet), wasTruncated: true }
  }

  const validModules = modules.reduce<DashboardModule[]>((acc, module) => {
    const validModule = validateModule(module)
    if (validModule) acc.push(validModule)
    return acc
  }, [])

  // Distinguish "user cleared the dashboard" (empty input preserved) from
  // "saved data is corrupt" (non-empty input where every entry failed validation —
  // e.g. a deploy renamed/removed every module type the user had). The corruption
  // case restores defaults so the user is not stranded with a blank dashboard.
  if (modules.length > 0 && validModules.length === 0) {
    return { valid: createDefaultModules(hasThematicBetasContentSet), wasTruncated: true }
  }

  return { valid: validModules, wasTruncated: validModules.length !== modules.length }
}

export const panelsSlice = createSlice({
  name: "dashboard/panels",
  initialState: {} as PanelsRootState,
  reducers: {
    addModule(
      state,
      action: PayloadAction<{ dashboardId: string, id: string, type: ModuleType, config?: ModuleConfig }>,
    ) {
      const { dashboardId, id, type, config = {} } = action.payload
      if (!isModuleTypeRegistered(type)) {
        log(`Cannot add module: type "${type}" is not registered`)
        return
      }
      const instance = state[dashboardId]
      if (!instance) return
      instance.modules.push({ id, type, config })
    },

    removeModule(
      state,
      action: PayloadAction<{ dashboardId: string, moduleId: string }>,
    ) {
      const { dashboardId, moduleId } = action.payload
      const instance = state[dashboardId]
      if (!instance) return
      instance.modules = instance.modules.filter((m) => m.id !== moduleId)
    },

    updateModule(
      state,
      action: PayloadAction<{ dashboardId: string, moduleId: string, updates: Partial<DashboardModule> }>,
    ) {
      const { dashboardId, moduleId, updates } = action.payload
      const instance = state[dashboardId]
      if (!instance) return
      const index = instance.modules.findIndex((m) => m.id === moduleId)
      if (index === -1) return

      const currentModule = instance.modules[index]
      if (!currentModule) return

      if ("type" in updates && updates.type !== undefined) {
        if (!isModuleTypeRegistered(updates.type)) {
          log(`Cannot update module: type "${updates.type}" is not registered`)
          return
        }
      }

      const { config: configUpdates, ...topLevelUpdates } = updates

      // Skip no-op writes — MUI DataGrid fires onDataGridStateChange on every
      // internal state change (filter, sort, column resize, pin-state init),
      // many of which are byte-identical to current. Without this guard each
      // call mutates the Immer draft and fans out re-renders across every
      // panels selector consumer.
      const topLevelChanged = Object.keys(topLevelUpdates).some(
        (key) => (currentModule as Record<string, unknown>)[key] !== (topLevelUpdates as Record<string, unknown>)[key],
      )
      const currentConfig = currentModule.config ?? {}
      const configChanged = configUpdates !== undefined && Object.keys(configUpdates).some(
        (key) => (currentConfig as Record<string, unknown>)[key] !== (configUpdates as Record<string, unknown>)[key],
      )
      if (!topLevelChanged && !configChanged) return

      Object.assign(currentModule, topLevelUpdates)
      if (configUpdates !== undefined) {
        currentModule.config = { ...currentModule.config, ...configUpdates }
      }
    },

    setPreferences(
      state,
      action: PayloadAction<{ dashboardId: string, modules?: DashboardModule[], hasThematicBetasContentSet?: boolean }>,
    ) {
      const { dashboardId, modules, hasThematicBetasContentSet } = action.payload
      const instance = ensureInstance(state, dashboardId)
      if (modules !== undefined) {
        const { valid, wasTruncated } = validateModules(modules, hasThematicBetasContentSet)
        if (wasTruncated) {
          info("Dashboard modules were truncated during validation")
        }
        instance.modules = valid
      }
      instance.isPreferencesLoaded = true
    },

    resetModulesToDefault(
      state,
      action: PayloadAction<{ dashboardId: string, hasThematicBetasContentSet?: boolean }>,
    ) {
      const { dashboardId, hasThematicBetasContentSet } = action.payload
      const instance = ensureInstance(state, dashboardId)
      instance.modules = createDefaultModules(hasThematicBetasContentSet)
    },
  },
})

export type ModuleUpdates = Partial<DashboardModule>
export default panelsSlice.reducer
